import {
  UserMetricsDataForGraphProcessing,
  UserMetricsParsedDetails,
  UserMetricsMasterFilters,
  UserMetricsParsedData,
  UserMetricsRawData,
  CalculatedUserMetricsGraphInfo,
  CalculatedUserMetricsDetailsTabInfo,
  Top3Apps,
  UserMetricsAggregatedViewsTable,
  MetricsMeasure,
  Metadata,
  HierarchyRow,
  ManagerHierarchyRow,
  ApplicationHierarchyRow,
  UserMetricsHomePageFinSuite
} from './UserMetricsInterfaces';
import { ColDef, ColGroupDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DAS_FIN_SUITE_WEBSITE, HIERARCHY_VIEW_MODE, HierarchyViewMode } from './UserMetricsConstants';
import { findCommonElements3 } from '../fintech-ops-dashboard/FinTechOpsUtils';
import { ALL_APPLICATIONS, FilterDateFormat_MMM_YYYY, MANAGER_ROSTER, OEEmptyStringMappings, SKYNET_ALL } from '../FinTechOpsConstants';
import { getLastFewMonthsIncludingCurrentMonth_AllowedByFinOps, sortedDateArrayMMMYYYYFormat } from 'src/utilities/DateTimeUtilities';
import moment from 'moment';
import React from 'react';
import { DashboardFilters, UserMetricsDashboardItems } from '../fintech-ops-dashboard/FinTechOpsDataInterfaces';
import { OptionItem, sortDistinctMonths_MM_YYYY } from '../FinTechOpsDataProcessingFunctions';
import { TableProps } from '@amzn/awsui-components-core';
import {
  USER_METRICS_APPLICATION_USER_VIEW_VISIBLE_CONTENT,
  USER_METRICS_APPLICATION_USER_VIEW__COLUMN_DEFINITIONS,
  USER_METRICS_FOOTPRINT_COLUMN_DEFINITIONS,
  USER_METRICS_FOOTPRINT_VISIBLE_CONTENT,
  USER_METRICS_SUB_APPLICATION_FOOTPRINT_COLUMN_DEFINITIONS,
  USER_METRICS_SUB_APPLICATION_FOOTPRINT_VISIBLE_CONTENT,
  USER_METRICS_SUB_APPLICATION_USER_VIEW_VISIBLE_CONTENT,
  USER_METRICS_SUB_APPLICATION_USER_VIEW__COLUMN_DEFINITIONS
} from './UserMetricsTableFilterConfig';
import { numberFormatter, aggregateMetricsData, getSortedArrayWithNumber, getUniqueFieldValues } from 'src/utilities/CommonUtilities';
import {
  APPLICATION_FOOTPRINT_COLUMN_LISTS,
  APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS,
  SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS,
  SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS,
  APPLICATION_USER_VIEW_COLUMN_LISTS,
  APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS,
  SUB_APPLICATION_USER_VIEW_COLUMN_LISTS,
  SUB_APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS,
  APPLICATION,
  USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST,
  USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST,
  USER_METRICS_DASHBOARD_COLUMN_LIST,
  USER_METRICS_DASHBOARD_MEASURE_COLUMN_LIST,
  USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST,
  USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST,
  USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST,
  USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST,
  USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST,
  DAS_FINTECH_ORG_MANAGER_ALIAS,
  MANAGER_TARGET_JOB_LEVELS
} from './UserMetricsConstants';

export function filterDataByCreateDates(rawData: UserMetricsRawData[]): UserMetricsRawData[] {
  const currentDate = moment();
  const lastSixMonthsIncludingCurrentMonth = getLastFewMonthsIncludingCurrentMonth_AllowedByFinOps();

  const included: UserMetricsRawData[] = [];
  const excluded: UserMetricsRawData[] = [];

  rawData.forEach((item) => {
    const createDate = moment(item.log_month, 'YYYY-MM-DD');
    if (createDate.isBetween(lastSixMonthsIncludingCurrentMonth, currentDate, 'day', '[]')) {
      included.push(item);
    } else {
      excluded.push(item);
    }
  });

  return included;
}

/**
 * Backfills missing user hierarchy data across raw records for the same user_login.
 *
 * Problem: Due to data refresh timing differences, some records for a user_login may
 * have null/empty values for hierarchy fields (user_manager_login_hierarchy,
 * user_manager_name_hierarchy, user_manager_job_level_hierarchy, team_size_rollup)
 * while other records for the same user have valid values.
 *
 * Solution: Single pass through the data with a deferred backfill list.
 * - As we iterate, we build a lookup map of known good hierarchy values per user_login.
 * - Records missing values are filled immediately if we've already seen good data for that user.
 * - Records seen before good data is found are added to a deferred list.
 * - After the pass, the deferred list is resolved using the now-populated lookup map.
 *
 * This mutates the input array in place for performance.
 *
 * @param rawData - Array of raw user metrics data to backfill (mutated in place)
 * @returns The same array, with hierarchy nulls filled where possible
 */
export const backfillUserHierarchyData = (rawData: UserMetricsRawData[]): UserMetricsRawData[] => {
  // Map of user_login -> known good hierarchy values
  const knownHierarchies = new Map<
    string,
    {
      user_manager_login_hierarchy: string;
      user_manager_name_hierarchy: string;
      user_manager_job_level_hierarchy: string;
      team_size_rollup: string;
    }
  >();

  // Indices of records that were seen before we found good values for their user_login
  const deferredIndices: number[] = [];

  const hasValidHierarchy = (item: UserMetricsRawData): boolean => {
    return !!(
      item.user_manager_login_hierarchy &&
      item.user_manager_name_hierarchy &&
      item.user_manager_job_level_hierarchy &&
      item.team_size_rollup
    );
  };

  const isMissingHierarchy = (item: UserMetricsRawData): boolean => {
    return (
      !item.user_manager_login_hierarchy || !item.user_manager_name_hierarchy || !item.user_manager_job_level_hierarchy || !item.team_size_rollup
    );
  };

  const applyHierarchy = (
    item: UserMetricsRawData,
    known: {
      user_manager_login_hierarchy: string;
      user_manager_name_hierarchy: string;
      user_manager_job_level_hierarchy: string;
      team_size_rollup: string;
    }
  ): void => {
    if (!item.user_manager_login_hierarchy) item.user_manager_login_hierarchy = known.user_manager_login_hierarchy;
    if (!item.user_manager_name_hierarchy) item.user_manager_name_hierarchy = known.user_manager_name_hierarchy;
    if (!item.user_manager_job_level_hierarchy) item.user_manager_job_level_hierarchy = known.user_manager_job_level_hierarchy;
    if (!item.team_size_rollup) item.team_size_rollup = known.team_size_rollup;
  };

  // Single pass through the data
  for (let i = 0; i < rawData.length; i++) {
    const item = rawData[i];
    const userLogin = item.user_login;

    if (!userLogin) continue;

    const existing = knownHierarchies.get(userLogin);

    if (existing) {
      // We already know good values for this user
      if (isMissingHierarchy(item)) {
        applyHierarchy(item, existing);
      }
    } else {
      // First time seeing this user_login
      if (hasValidHierarchy(item)) {
        // This record has good data - store it
        knownHierarchies.set(userLogin, {
          user_manager_login_hierarchy: item.user_manager_login_hierarchy,
          user_manager_name_hierarchy: item.user_manager_name_hierarchy,
          user_manager_job_level_hierarchy: item.user_manager_job_level_hierarchy,
          team_size_rollup: item.team_size_rollup
        });
      } else {
        // This record is missing data and we haven't seen good data yet - defer it
        deferredIndices.push(i);
      }
    }
  }

  // Resolve deferred records using the now-populated lookup map
  for (const index of deferredIndices) {
    const item = rawData[index];
    const known = knownHierarchies.get(item.user_login);
    if (known) {
      applyHierarchy(item, known);
    }
  }

  return rawData;
};

export const returnUsefulRawUserMetricsData = (userMetricsRawDetails: UserMetricsRawData[], lastUpdatedAt: Metadata): UserMetricsParsedDetails => {
  const returnItems: UserMetricsParsedData[] = [];

  const last6MonthData = filterDataByCreateDates(userMetricsRawDetails);

  // Backfill missing hierarchy data before parsing
  backfillUserHierarchyData(last6MonthData);

  last6MonthData.forEach((item: UserMetricsRawData) => {
    const logMonth_YYYY_MM_DD = moment(item.log_month, 'YYYY-MM-DD');

    returnItems.push({
      team_name: item.team_name || OEEmptyStringMappings.Unassigned_Team,

      manager_name_hierarchy_concat: item.supervisor_name_hierarchy
        ? item.supervisor_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager],
      manager_login_hierarchy_concat: item.supervisor_login_name_hierarchy
        ? item.supervisor_login_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Login],

      manager_name: item.supervisor_name_hierarchy ? item.supervisor_name_hierarchy.split(':')[0] : OEEmptyStringMappings.Unassigned_Manager,
      manage_login: item.supervisor_login_name_hierarchy
        ? item.supervisor_login_name_hierarchy.split(':')[0]
        : OEEmptyStringMappings.Unassigned_Login,

      application: item.display_sub_application_name || item.application, // Use display name from mapping if available
      application_type: item.application_type,

      user_manager_login_hierarchy: item.user_manager_login_hierarchy
        ? item.user_manager_login_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager_Login],
      user_manager_name_hierarchy: item.user_manager_name_hierarchy
        ? item.user_manager_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager],
      user_manager_job_level_hierarchy: item.user_manager_job_level_hierarchy ? item.user_manager_job_level_hierarchy.split(':') : ['-1'],
      team_size_rollup: item.team_size_rollup ? item.team_size_rollup.split(':').map(Number) : [0],

      user_job_level: item.user_job_level || 0,

      log_month: item.log_month,
      create_date_formatted: logMonth_YYYY_MM_DD.format(FilterDateFormat_MMM_YYYY),
      created_on_month: logMonth_YYYY_MM_DD.month() + 1, // Add 1 to get one-based month number

      measure_value: parseInt(item.measure_value?.toString()),
      user_login: item.user_login,
      active_since: item.active_since,
      last_active: item.last_active,
      user_login_count_distinct: 0 // Will be calculated during aggregation
    } as UserMetricsParsedData);
  });

  return {
    metadata: lastUpdatedAt,
    data: returnItems
  } as UserMetricsParsedDetails;
};

export const applyFiltersUserMetrics = (
  items: UserMetricsParsedData[],
  filters: UserMetricsMasterFilters
): [UserMetricsParsedData[], UserMetricsParsedData[]] => {
  const filteredHistoricData: UserMetricsParsedData[] = items.filter((item: UserMetricsParsedData) => {
    // Base filters
    const baseFilters =
      findCommonElements3(filters.managers, item.manager_name_hierarchy_concat) &&
      filters.team.includes(item.team_name) &&
      filters.application_type.includes(item.application_type) &&
      filters.application_name.includes(item.application) &&
      filters.user_login.includes(item.user_login) &&
      findCommonElements3(filters.user_manager, item.user_manager_name_hierarchy);

    return baseFilters;
  });

  const filteredRecentData: UserMetricsParsedData[] = filteredHistoricData.filter((item) => {
    return filters.month.includes(moment(item.log_month).format(FilterDateFormat_MMM_YYYY));
  });

  return [filteredHistoricData, filteredRecentData];
};

export const applyDashboardFiltersUserMetrics = (items: UserMetricsParsedData[], filters: DashboardFilters): UserMetricsParsedData[] => {
  return items.filter((item: UserMetricsParsedData) => {
    return (
      filters.team_name.includes(item.team_name) &&
      findCommonElements3(filters.managers, item.manager_name_hierarchy_concat) &&
      item.application !== ALL_APPLICATIONS
    );
  });
};

export const getDashboardFiltersFromUserMetrics = (items: UserMetricsParsedData[]): DashboardFilters => {
  const dashboardTicketFilters: DashboardFilters = {
    team_name: [],
    managers: [],
    assignees: []
  };

  items.forEach((item: UserMetricsParsedData) => {
    if (!dashboardTicketFilters.team_name.includes(item.team_name)) {
      dashboardTicketFilters.team_name.push(item.team_name);
    }

    if (!dashboardTicketFilters.managers.includes(item.manager_name)) {
      dashboardTicketFilters.managers.push(item.manager_name);
    }
  });
  return dashboardTicketFilters;
};

export const createUserMetricsMasterFilterData = (items: UserMetricsParsedData[]): UserMetricsMasterFilters => {
  const userMetricsMasterFilterData: UserMetricsMasterFilters = {
    managers: [],
    team: [],
    application_type: [],
    application_name: [],
    month: [],
    user_login: [],
    user_manager: [],
    org_include: true
  };

  items.forEach((item: UserMetricsParsedData) => {
    if (item.application === MANAGER_ROSTER) {
      return;
    }
    if (!userMetricsMasterFilterData.managers.includes(item.manager_name)) {
      userMetricsMasterFilterData.managers.push(item.manager_name);
    }
    if (!userMetricsMasterFilterData.team.includes(item.team_name)) {
      userMetricsMasterFilterData.team.push(item.team_name);
    }
    if (!userMetricsMasterFilterData.application_type.includes(item.application_type)) {
      userMetricsMasterFilterData.application_type.push(item.application_type);
    }
    if (!userMetricsMasterFilterData.application_name.includes(item.application) && item.application !== ALL_APPLICATIONS) {
      userMetricsMasterFilterData.application_name.push(item.application);
    }
    if (!userMetricsMasterFilterData.month.includes(item.create_date_formatted)) {
      userMetricsMasterFilterData.month.push(item.create_date_formatted);
    }
    if (!userMetricsMasterFilterData.user_login.includes(item.user_login)) {
      userMetricsMasterFilterData.user_login.push(item.user_login);
    }
    addUniqueManagersToFilter(
      item.user_manager_name_hierarchy,
      userMetricsMasterFilterData.user_manager,
      item.user_login,
      item.user_manager_login_hierarchy
    );
  });
  return userMetricsMasterFilterData;
};

/**
 * Adds unique managers from a hierarchy array to the filter list,
 * excluding the user's own login
 */
const addUniqueManagersToFilter = (hierarchy: string[], existingManagers: string[], userLogin: string, hierarchy_login: string[]): void => {
  for (let i = 0; i < hierarchy.length; i++) {
    if (hierarchy[i] && hierarchy_login[i] && hierarchy_login[i] !== userLogin && !existingManagers.includes(hierarchy[i])) {
      existingManagers.push(hierarchy[i]);
    }
  }
};

export const createNonFinTechOrgData = (items: UserMetricsParsedData[]): [UserMetricsParsedData[], UserMetricsParsedData[]] => {
  const finTechUserItems: UserMetricsParsedData[] = [];
  const nonFinTechUserItems: UserMetricsParsedData[] = [];
  items.forEach((item) => {
    if (item.user_manager_login_hierarchy.includes(DAS_FINTECH_ORG_MANAGER_ALIAS)) {
      finTechUserItems.push(item);
    } else {
      nonFinTechUserItems.push(item);
    }
  });
  return [finTechUserItems, nonFinTechUserItems];
};

export function returnSelectedAllAppNameCustom(items: string[]): OptionItem[] {
  const itemsOptions: OptionItem[] = [];
  let valueForSelector = 1;
  if (items != undefined && items.length > 0) {
    items.forEach((element) => {
      if (element != SKYNET_ALL && element != 'common') {
        itemsOptions.push({ label: element, value: element });
      }
      valueForSelector++;
    });
  }
  return itemsOptions;
}

// Calculations for FinTechOps DashBoard User Metrics section
export const createOpsUserMetricsData = (appliedFiltersData: UserMetricsParsedData[]): UserMetricsDashboardItems => {
  const considerableData = appliedFiltersData.filter((item: UserMetricsParsedData) => {
    return item.application !== ALL_APPLICATIONS && item.application !== 'common' && !item.application.toLowerCase().includes('skynet');
  });

  const distinctApplications: string[] = [];
  const distinctMonths_YYYY_MM_DD: string[] = [];
  const currentMonth_YYYY_MM_DD = moment().startOf('month').format('YYYY-MM-DD');

  let appplicationViewCount = 0;

  let currentMonthAppWithMoreUniqueUsersCount = 0;
  let currentMonthAppWithMoreUniqueUsers = '';

  let currentMonthMostViewed = 0;
  let currentMonthMostViewedApplication = '';

  const aggregatedData = aggregateMetricsData(USER_METRICS_DASHBOARD_COLUMN_LIST, considerableData, USER_METRICS_DASHBOARD_MEASURE_COLUMN_LIST) || [];

  aggregatedData.forEach((item: UserMetricsParsedData) => {
    //changed logic to application_type wise top application and application count
    const { application_type, log_month } = item;
    appplicationViewCount += item.measure_value;

    if (!distinctApplications.includes(application_type)) {
      distinctApplications.push(application_type);
    }

    if (!distinctMonths_YYYY_MM_DD.includes(log_month)) {
      distinctMonths_YYYY_MM_DD.push(log_month);
    }

    if (item.log_month === currentMonth_YYYY_MM_DD && item.user_login_count_distinct > currentMonthAppWithMoreUniqueUsersCount) {
      currentMonthAppWithMoreUniqueUsersCount = item.user_login_count_distinct;
      currentMonthAppWithMoreUniqueUsers = item.application_type;
    }

    if (item.log_month === currentMonth_YYYY_MM_DD && item.measure_value > currentMonthMostViewed) {
      currentMonthMostViewed = item.measure_value;
      currentMonthMostViewedApplication = item.application_type;
    }
  });

  let noOfDays = 0;
  distinctMonths_YYYY_MM_DD.forEach((eachDistinctMonth: string) => {
    if (eachDistinctMonth !== currentMonth_YYYY_MM_DD) {
      const daysInThisMOnth = moment(eachDistinctMonth, 'YYYY-MM-DD').daysInMonth();
      noOfDays += daysInThisMOnth;
    } else {
      const daysInThisMOnth = moment(eachDistinctMonth, 'YYYY-MM-DD').daysInMonth() - moment().date();
      noOfDays += daysInThisMOnth;
    }
  });

  const avgViewCountPerApplication = appplicationViewCount / (noOfDays * distinctApplications?.length);

  return {
    total_applications: distinctApplications?.length || 0,
    avg_view_per_application: isNaN(avgViewCountPerApplication) ? '0' : `${Number(avgViewCountPerApplication).toFixed(0)}`, // Avergae viewcount of Application excluding "All Applications" "Skynet" and "common" divided by total number of days  `${Number(avgViewCountPerApplication).toFixed(0)}`, // Avergae viewcount of Application excluding "All Applications" "Skynet" and "common" divided by total number of days
    app_most_unique_users: currentMonthAppWithMoreUniqueUsers,
    most_viewed_application: currentMonthMostViewedApplication
  };
};

export const createUserMetricsGraphDataV2 = (actualUserMetricsParsedData: UserMetricsParsedData[]): CalculatedUserMetricsGraphInfo => {
  const totalAppViewByTeam: UserMetricsDataForGraphProcessing[] = getTotalApplicationViewByTeam(actualUserMetricsParsedData);

  const top5AppViewership: UserMetricsDataForGraphProcessing[] = getTop5ApplicationsByMonth(actualUserMetricsParsedData);

  const uniqueUsersByAppAndMonth: UserMetricsDataForGraphProcessing[] = getTotalUniqueUsersByApplicationAndMonth(actualUserMetricsParsedData);
  const totalViewByApplicationType: UserMetricsDataForGraphProcessing[] = getTotalViewsByApplicationType(actualUserMetricsParsedData);

  return {
    appViewerShipTeamGraph: totalAppViewByTeam,
    top5AppViewershipGraph: top5AppViewership,

    uniqueUsersByAppGraph: uniqueUsersByAppAndMonth,
    totalAppViewsGraph: totalViewByApplicationType
  };
};

// Total Application Views by Team
export function getTotalApplicationViewByTeam(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  userMetricsMasterFilters?: UserMetricsMasterFilters,
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  const result: UserMetricsDataForGraphProcessing[] = [];

  const excludingAllApplications = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application !== ALL_APPLICATIONS);

  let filteredData: UserMetricsParsedData[];

  if (businessReportingMode) {
    // Business reporting mode - use pre-filtered data and exclude unmapped teams
    filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => item.team_name !== 'Unmapped' && item.team_name?.trim() !== '');
  } else {
    // Original user metrics mode - ensure filters exist
    if (!userMetricsMasterFilters) {
      filteredData = excludingAllApplications;
    } else {
      filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => {
        return (
          findCommonElements3(userMetricsMasterFilters.managers, item.manager_name_hierarchy_concat) &&
          userMetricsMasterFilters.team.includes(item.team_name) &&
          userMetricsMasterFilters.application_type.includes(item.application_type) &&
          userMetricsMasterFilters.application_name.includes(item.application) &&
          userMetricsMasterFilters.month.includes(moment(item.log_month).format(FilterDateFormat_MMM_YYYY)) &&
          userMetricsMasterFilters.user_login.includes(item.user_login)
        );
      });
    }
  }

  const dataMap = new Map<string, number>();

  for (const item of filteredData) {
    const team = item.team_name;
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")

    const key = `${team}_${month}`;
    const count = item.measure_value;

    if (dataMap.has(key)) {
      dataMap.set(key, dataMap.get(key)! + count);
    } else {
      dataMap.set(key, count);
    }
  }

  dataMap.forEach((count, key) => {
    const [team, month] = key.split('_');
    // Keep YYYY-MM format for business reporting, use YYYYMM for user metrics
    const categoryFormat = businessReportingMode ? month : month.replace('-', '');
    result.push({ title: team, category: categoryFormat, count });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Top 5 Application - Unique Users
export function getTop5ApplicationsByMonth(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  userMetricsMasterFilters?: UserMetricsMasterFilters,
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  const result: UserMetricsDataForGraphProcessing[] = [];
  const excludingAllApplications = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application !== ALL_APPLICATIONS);

  let filteredData: UserMetricsParsedData[];

  if (businessReportingMode || !userMetricsMasterFilters) {
    // Business reporting mode or no filters - use pre-filtered data
    filteredData = excludingAllApplications;
  } else {
    // Original user metrics mode
    filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => {
      return (
        findCommonElements3(userMetricsMasterFilters.managers, item.manager_name_hierarchy_concat) &&
        userMetricsMasterFilters.team.includes(item.team_name) &&
        userMetricsMasterFilters.application_type.includes(item.application_type) &&
        userMetricsMasterFilters.application_name.includes(item.application) &&
        userMetricsMasterFilters.month.includes(moment(item.log_month).format(FilterDateFormat_MMM_YYYY)) &&
        userMetricsMasterFilters.user_login.includes(item.user_login)
      );
    });
  }
  const aggregatedData =
    aggregateMetricsData(
      USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST,
      filteredData,
      USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST
    ) || [];
  const dataMap = new Map<string, Map<string, number>>();
  // for (const item of filteredData) {
  for (const item of aggregatedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const application = item.application;
    const distinctUsers = item.user_login_count_distinct;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(application)) {
        monthMap.set(application, monthMap.get(application)! + distinctUsers);
      } else {
        monthMap.set(application, distinctUsers);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(application, distinctUsers);
      dataMap.set(month, monthMap);
    }
  }
  // Iterate over the map and get the top 5 applications for each month
  dataMap.forEach((monthMap, month) => {
    const sortedApplications = [...monthMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

    for (const [application, count] of sortedApplications) {
      result.push({
        title: application,
        category: month,
        count: count
      });
    }
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Total Unique Users by Application Type
// include only All_Apps
export function getTotalUniqueUsersByApplicationAndMonth(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  if (businessReportingMode) {
    // Business reporting mode or no filters - use ALL_APPLICATIONS entries only
    actualUserMetricsParsedData = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application === ALL_APPLICATIONS);
  }

  const result: UserMetricsDataForGraphProcessing[] = [];

  const dataMap = new Map<string, Map<string, number>>();

  const aggregatedData =
    aggregateMetricsData(
      USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST,
      actualUserMetricsParsedData,
      USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST
    ) || [];

  for (const item of aggregatedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const applicationType = item.application_type;
    const distinctUsers = item.user_login_count_distinct;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(applicationType)) {
        monthMap.set(applicationType, monthMap.get(applicationType)! + distinctUsers);
      } else {
        monthMap.set(applicationType, distinctUsers);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(applicationType, distinctUsers);
      dataMap.set(month, monthMap);
    }
  }

  // Iterate over the map and get the total unique user count for each application type in each month
  dataMap.forEach((monthMap, month) => {
    monthMap.forEach((count, applicationType) => {
      result.push({
        title: applicationType,
        category: month,
        count
      });
    });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Total Views by Application Type
export function getTotalViewsByApplicationType(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  if (businessReportingMode) {
    // Business reporting mode or no filters - use ALL_APPLICATIONS entries only
    actualUserMetricsParsedData = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application === ALL_APPLICATIONS);
  }

  const result: UserMetricsDataForGraphProcessing[] = [];

  const dataMap = new Map<string, Map<string, number>>();

  for (const item of actualUserMetricsParsedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const applicationType = item.application_type;
    const measureValue = item.measure_value;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(applicationType)) {
        monthMap.set(applicationType, monthMap.get(applicationType)! + measureValue);
      } else {
        monthMap.set(applicationType, measureValue);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(applicationType, measureValue);
      dataMap.set(month, monthMap);
    }
  }

  // Iterate over the map and get the total unique user count for each application type in each month
  dataMap.forEach((monthMap, month) => {
    monthMap.forEach((count, applicationType) => {
      result.push({
        title: applicationType,
        category: month,
        count
      });
    });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

//Function to get user metrics details tab data
export const createUserMetricsDetailsTabData = (
  appliedFiltersData: UserMetricsParsedData[],
  filteredWithoutMonthData: UserMetricsParsedData[],
  month: string[]
): CalculatedUserMetricsDetailsTabInfo => {
  const considerableFilterData: UserMetricsParsedData[] = appliedFiltersData.filter((item) => item.application !== ALL_APPLICATIONS);

  const userMetricsDetailsTabInfo: CalculatedUserMetricsDetailsTabInfo = {
    applicationFootprintContainer: {
      top3Apps: [],
      totalViews: 0,
      currentMonth: ''
    },
    aggregatedViewsTable: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    aggregatedUsersTable: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    aggregatedSubApplicationViewsTableData: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    aggregatedSubApplicationUniqueUsersTableData: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    applicationFootprintTable: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    subApplicationFootprintTable: {
      visibleColumns: [],
      columnData: [],
      rowData: []
    },
    applicationUserViewTable: {
      visibleColumns: USER_METRICS_APPLICATION_USER_VIEW_VISIBLE_CONTENT,
      columnData: USER_METRICS_APPLICATION_USER_VIEW__COLUMN_DEFINITIONS,
      rowData: []
    },
    subApplicationUserViewTable: {
      visibleColumns: USER_METRICS_SUB_APPLICATION_USER_VIEW_VISIBLE_CONTENT,
      columnData: USER_METRICS_SUB_APPLICATION_USER_VIEW__COLUMN_DEFINITIONS,
      rowData: []
    }
  };

  let totalViewCount = 0;

  considerableFilterData.forEach((item) => {
    totalViewCount += item.measure_value;
  });
  const top3Applications = getTop3ApplicationsInCurrentMonth(considerableFilterData, filteredWithoutMonthData);
  const sortedMonths = sortedDateArrayMMMYYYYFormat(getUniqueFieldValues(considerableFilterData, 'create_date_formatted'));
  const currentMonthValue = moment().format(FilterDateFormat_MMM_YYYY);
  // consider current month only if its the only value selected in filter
  const currentMonth = sortedMonths.length > 1 && sortedMonths.includes(currentMonthValue) ? sortedMonths[1] : sortedMonths[0];

  //Aggregate the sub application for each month with views
  const aggregatedSubApplicationViewsTableData = getAggregatedUserMetricsBySubApplication(
    considerableFilterData,
    USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST,
    SUB_APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS
  );
  //Aggregate the sub application for each month with unique users
  const aggregatedSubApplicationUniqueUsersTableData = getAggregatedUserMetricsBySubApplication(
    considerableFilterData,
    USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST,
    USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST
  );
  const aggregatedUserMetricsByApplicationWithUniqueUsers = getAggregatedUserMetricsByApplicationWithUniqueUsers(considerableFilterData);
  const aggregatedUserMetricsByApplicationWithUserCount = getAggregatedUserMetricsByApplicationWithUserCount(considerableFilterData);
  const applicationFootprintTableData = aggregateMetricsData(
    APPLICATION_FOOTPRINT_COLUMN_LISTS,
    appliedFiltersData,
    APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS
  );
  const appFootprintTable = applicationFootprintTableData
    ? getAppFootPrintTableDetails(applicationFootprintTableData, month, 'application')
    : userMetricsDetailsTabInfo.applicationFootprintTable;

  const subApplicationFootprintTableData = aggregateMetricsData(
    SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS,
    appliedFiltersData,
    SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS
  );
  const subAppFootprintTable = subApplicationFootprintTableData
    ? getAppFootPrintTableDetails(subApplicationFootprintTableData, month, 'subApplication')
    : userMetricsDetailsTabInfo.subApplicationFootprintTable;

  const applicationUserViewTableData = aggregateMetricsData(
    APPLICATION_USER_VIEW_COLUMN_LISTS,
    appliedFiltersData,
    APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS
  );
  userMetricsDetailsTabInfo.applicationUserViewTable.rowData = applicationUserViewTableData ? applicationUserViewTableData : [];
  const subApplicationUserViewTableData = aggregateMetricsData(
    SUB_APPLICATION_USER_VIEW_COLUMN_LISTS,
    appliedFiltersData,
    SUB_APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS
  );
  userMetricsDetailsTabInfo.subApplicationUserViewTable.rowData = subApplicationUserViewTableData ? subApplicationUserViewTableData : [];
  return {
    ...userMetricsDetailsTabInfo,
    applicationFootprintContainer: {
      ...userMetricsDetailsTabInfo.applicationFootprintContainer,
      totalViews: totalViewCount,
      top3Apps: top3Applications,
      currentMonth: currentMonth
    },
    aggregatedViewsTable: aggregatedUserMetricsByApplicationWithUserCount,
    aggregatedUsersTable: aggregatedUserMetricsByApplicationWithUniqueUsers,
    aggregatedSubApplicationViewsTableData: aggregatedSubApplicationViewsTableData,
    aggregatedSubApplicationUniqueUsersTableData: aggregatedSubApplicationUniqueUsersTableData,
    applicationFootprintTable: appFootprintTable,
    subApplicationFootprintTable: subAppFootprintTable,
    applicationUserViewTable: userMetricsDetailsTabInfo.applicationUserViewTable,
    subApplicationUserViewTable: userMetricsDetailsTabInfo.subApplicationUserViewTable
  };
};

const getTop3ApplicationsInCurrentMonth = (
  userMetricsData: UserMetricsParsedData[],
  actualUserMetricsParsedData: UserMetricsParsedData[]
): Top3Apps[] => {
  const result: Top3Apps[] = [];
  //initializing maxMonth & previousMonth to default value to handle empty userMetricsData
  let maxMonth = moment().month();
  let previousMonth = moment().month();
  if (userMetricsData.length > 0) {
    const sortedMonths = sortedDateArrayMMMYYYYFormat(getUniqueFieldValues(userMetricsData, 'create_date_formatted'));
    const currentMonthValue = moment().format(FilterDateFormat_MMM_YYYY);
    // consider the 0th index month only if its the only value selected in filter
    const maxMonthValue = sortedMonths.length > 1 && sortedMonths.includes(currentMonthValue) ? sortedMonths[1] : sortedMonths[0];
    maxMonth = moment(maxMonthValue, FilterDateFormat_MMM_YYYY).month() + 1;
    previousMonth = moment(maxMonthValue, FilterDateFormat_MMM_YYYY).subtract(1, 'months').month() + 1;
  }

  const dataMap = new Map<string, number>();

  const currentMonthFilteredData = userMetricsData.filter((item) => item.created_on_month === maxMonth);

  for (const item of currentMonthFilteredData) {
    const application = item.application;
    const count = item.measure_value;
    if (dataMap.has(application)) {
      dataMap.set(application, dataMap.get(application)! + count);
    } else {
      dataMap.set(application, count);
    }
  }

  const sortedApplications = [...dataMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  for (const [application, count] of sortedApplications) {
    result.push({
      application: application,
      views: count,
      MoM: getMoMForThisAppInPreviousMonth(application, count, previousMonth, actualUserMetricsParsedData)
    } as Top3Apps);
  }
  return result;
};

const getMoMForThisAppInPreviousMonth = (
  application: string,
  currentMonthCount: number,
  previousMontnNumber: number,
  userMetricsData: UserMetricsParsedData[]
) => {
  const currentMonthFilteredData = userMetricsData.filter(
    (item) => item.created_on_month === previousMontnNumber && item.application === application
  );

  let previousMonthCount = 0;
  currentMonthFilteredData.forEach((item) => {
    previousMonthCount = previousMonthCount + item.measure_value;
  });

  return currentMonthCount - previousMonthCount;
};

const getAggregatedUserMetricsByApplicationWithUniqueUsers = (userMetricsData: UserMetricsParsedData[]): UserMetricsAggregatedViewsTable => {
  const aggregatedData: Record<string, any> = {};

  const aggregatedDataValue =
    aggregateMetricsData(
      USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST,
      userMetricsData,
      USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST
    ) || [];

  let distinctMonths: string[] = [];

  for (const entry of aggregatedDataValue) {
    const { application_type, create_date_formatted, user_login_count_distinct } = entry;

    // Ensure month is in the distinct array
    if (!distinctMonths.includes(create_date_formatted)) {
      distinctMonths.push(create_date_formatted);
    }

    const key = `${application_type}`;

    if (aggregatedData[key]) {
      aggregatedData[key][create_date_formatted] = (aggregatedData[key][create_date_formatted] || 0) + user_login_count_distinct;
    } else {
      // Otherwise, create a new entry with userCount for the corresponding month
      aggregatedData[key] = {
        application_type,
        [create_date_formatted]: user_login_count_distinct
      };
    }
  }

  // Convert the aggregation object into an array of aggregated data
  const aggregatedArray = Object.values(aggregatedData);

  distinctMonths = sortDistinctMonths_MM_YYYY(distinctMonths);

  // Generate the column definition dynamically
  const columnDefinition: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'application_type',
      header: 'Application',
      sortingField: 'application_type',
      width: 150,
      cell: (item: any) => <>{item?.application_type}</>,
      isRowHeader: true
    },
    ...distinctMonths.map((month) => ({
      id: month,
      header: month,
      sortingField: month,
      width: 125,
      cell: (item: any) => <>{item[month] ? numberFormatter(item[month]) : 0}</>
    }))
  ];

  const visbleColumns: string[] = columnDefinition.map((item) => item.id || '');

  return {
    rowData: aggregatedArray,
    columnData: columnDefinition,
    visibleColumns: visbleColumns
  };
};

const getAggregatedUserMetricsByApplicationWithUserCount = (userMetricsData: UserMetricsParsedData[]): UserMetricsAggregatedViewsTable => {
  const aggregatedData: Record<string, any> = {};

  // Initialize an array to store distinct months
  let distinctMonths: string[] = [];

  for (const entry of userMetricsData) {
    const { application_type, create_date_formatted, measure_value } = entry;

    // Ensure month is in the distinct array
    if (!distinctMonths.includes(create_date_formatted)) {
      distinctMonths.push(create_date_formatted);
    }

    // Create a unique key based on application and application_type
    const key = `${application_type}`;

    // Check if an entry with this key already exists
    if (aggregatedData[key]) {
      // If it exists, add userCount to the existing entry for the corresponding month
      aggregatedData[key][create_date_formatted] = (aggregatedData[key][create_date_formatted] || 0) + measure_value;
    } else {
      // Otherwise, create a new entry with userCount for the corresponding month
      aggregatedData[key] = {
        application_type,
        [create_date_formatted]: measure_value
      };
    }
  }

  // Convert the aggregation object into an array of aggregated data
  const aggregatedArray = Object.values(aggregatedData);

  distinctMonths = sortDistinctMonths_MM_YYYY(distinctMonths);

  // Generate the column definition dynamically
  const columnDefinition: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'application_type',
      header: 'Application',
      sortingField: 'application_type',
      cell: (item: any) => <>{item?.application_type}</>,
      isRowHeader: true
    },
    ...distinctMonths.map((month) => ({
      id: month,
      header: month,
      sortingField: month,
      cell: (item: any) => <>{item[month] ? numberFormatter(item[month]) : 0}</>
    }))
  ];

  const visbleColumns: string[] = columnDefinition.map((item) => item.id || '');

  return {
    rowData: aggregatedArray,
    columnData: columnDefinition,
    visibleColumns: visbleColumns
  };
};

//Function to aggregated the sub application for each month by views or unique users
const getAggregatedUserMetricsBySubApplication = (
  userMetricsData: UserMetricsParsedData[],
  columnList: string[],
  measureColumnList: MetricsMeasure[]
): UserMetricsAggregatedViewsTable => {
  const aggregatedData: Record<string, any> = {};
  const aggregatedDataValue = aggregateMetricsData(columnList, userMetricsData, measureColumnList) || [];

  let distinctMonths: string[] = [];

  for (const entry of aggregatedDataValue) {
    const application = entry.application;
    const application_type = entry.application_type;
    const create_date_formatted = entry.create_date_formatted;
    let measureColumn = '';
    if (Object.prototype.hasOwnProperty.call(entry, 'user_login_count_distinct')) {
      measureColumn = entry.user_login_count_distinct;
    } else if (Object.prototype.hasOwnProperty.call(entry, 'measure_value')) {
      measureColumn = entry.measure_value;
    }

    // Ensure month is in the distinct array
    if (!distinctMonths.includes(create_date_formatted)) {
      distinctMonths.push(create_date_formatted);
    }

    const key = `${application}-${application_type}`;

    if (aggregatedData[key]) {
      aggregatedData[key][create_date_formatted] = (aggregatedData[key][create_date_formatted] || 0) + measureColumn;
    } else {
      // Otherwise, create a new entry with userCount for the corresponding month
      aggregatedData[key] = {
        application,
        application_type,
        [create_date_formatted]: measureColumn
      };
    }
  }

  // Convert the aggregation object into an array of aggregated data
  const aggregatedArray = Object.values(aggregatedData);

  distinctMonths = sortDistinctMonths_MM_YYYY(distinctMonths);

  // Generate the column definition dynamically
  const columnDefinition: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'application',
      header: 'Sub Application',
      sortingField: 'application',
      width: 150,
      cell: (item: any) => <>{item?.application}</>,
      isRowHeader: true
    },
    {
      id: 'application_type',
      header: 'Application',
      sortingField: 'application_type',
      width: 150,
      cell: (item: any) => <>{item?.application_type}</>,
      isRowHeader: true
    },
    ...distinctMonths.map((month) => ({
      id: month,
      header: month,
      sortingField: month,
      width: 125,
      cell: (item: any) => <>{item[month] ? numberFormatter(item[month]) : 0}</>
    }))
  ];

  const visbleColumns: string[] = columnDefinition.map((item) => item.id || '');

  return {
    rowData: aggregatedArray,
    columnData: columnDefinition,
    visibleColumns: visbleColumns
  };
};

// Total Application & Sub Application Footprint Details
const getAppFootPrintTableDetails = (
  userMetricsData: UserMetricsParsedData[],
  monthList: string[],
  type: string
): UserMetricsAggregatedViewsTable => {
  let footPrintTableData = [];
  const userMetricsDataArr: any = [];
  let numberofDays = 0;
  numberofDays = monthList.reduce((totalDays, month) => totalDays + moment(month, FilterDateFormat_MMM_YYYY).daysInMonth(), 0);

  //calculating avg page views per day
  userMetricsData.forEach(function (item) {
    const obj: any = item;
    const avgPageViews = (item.measure_value / (item.user_login_count_distinct * numberofDays)).toFixed(2);
    obj['avgPageViewsPerDay'] = parseFloat(avgPageViews);
    obj['distinct_users'] = item['user_login_count_distinct'];
    userMetricsDataArr.push(obj);
  });

  footPrintTableData = userMetricsDataArr.map((item: any) => {
    if (type === APPLICATION) {
      return {
        team_name: item.team_name,
        app_type: item.application_type,
        distinctUsers: item.distinct_users,
        userCount: item.measure_value,
        avgPageViewsPerDay: item.avgPageViewsPerDay,
        active_since: item.active_since
      };
    } else {
      return {
        team_name: item.team_name,
        app_type: item.application_type,
        app_name: item.application,
        distinctUsers: item.distinct_users,
        userCount: item.measure_value,
        avgPageViewsPerDay: item.avgPageViewsPerDay,
        active_since: item.active_since
      };
    }
  });
  if (type === APPLICATION) {
    return {
      rowData: footPrintTableData?.sort((a: any, b: any) => b.distinctUsers - a.distinctUsers) || [],
      columnData: USER_METRICS_FOOTPRINT_COLUMN_DEFINITIONS,
      visibleColumns: USER_METRICS_FOOTPRINT_VISIBLE_CONTENT
    } as UserMetricsAggregatedViewsTable;
  } else {
    return {
      rowData: footPrintTableData?.sort((a: any, b: any) => b.distinctUsers - a.distinctUsers) || [],
      columnData: USER_METRICS_SUB_APPLICATION_FOOTPRINT_COLUMN_DEFINITIONS,
      visibleColumns: USER_METRICS_SUB_APPLICATION_FOOTPRINT_VISIBLE_CONTENT
    } as UserMetricsAggregatedViewsTable;
  }
};

/**
 * Builds hierarchical data for the Manager Hierarchy Tab.
 * Creates a tree structure where managers (L7-L11) are parent nodes and application types are children.
 * Each user's metrics (views from measure_value) are attributed to ALL L7+ managers in their hierarchy.
 * Unique users are tracked per manager+app combination using Sets to ensure proper deduplication.
 *
 * Manager Roster Integration:
 * Managers from the roster data (who have no user metrics) are included only if they share
 * a common ancestor with managers who have actual metrics data. This ensures team_size
 * visibility for L7+ level managers even when they have no direct application usage.
 *
 * @param userMetricsData - Parsed user metrics data with hierarchy information
 * @param targetLevels - Array of job levels to include (default: [7, 8, 9, 10, 11])
 * @param useApplicationType - If true, aggregates by application_type; if false, uses application (sub-app). Default: false
 * @param managerRosterData - Optional manager roster data for managers with no user metrics
 * @returns Array of HierarchyRow objects for AG-Grid tree data
 */
export const buildManagerHierarchyData = (
  userMetricsData: UserMetricsParsedData[],
  targetLevels: number[] = MANAGER_TARGET_JOB_LEVELS,
  useApplicationType = false,
  managerRosterData?: UserMetricsParsedData[]
): HierarchyRow[] => {
  // Filter out ALL_APPLICATIONS entries
  const filteredData = userMetricsData.filter((item) => item.application !== ALL_APPLICATIONS);

  // Track all months present in the data
  const allMonths = new Set<string>();

  // Map structure: manager_login -> { manager info, apps: Map<application, { views, users Set, monthlyData }>, allUsers Set, monthlyData }
  const managerMap = new Map<
    string,
    {
      login: string;
      name: string;
      level: number;
      path: string[];
      team_size: number;
      apps: Map<string, { views: number; users: Set<string>; monthlyViews: Map<string, number>; monthlyUsers: Map<string, Set<string>> }>;
      allUsers: Set<string>;
      monthlyViews: Map<string, number>;
      monthlyUsers: Map<string, Set<string>>;
    }
  >();

  // Process each user record
  for (const record of filteredData) {
    const {
      application,
      application_type,
      measure_value,
      user_login,
      team_size_rollup,
      create_date_formatted,
      user_manager_login_hierarchy,
      user_manager_name_hierarchy,
      user_manager_job_level_hierarchy
    } = record;

    // Track month
    allMonths.add(create_date_formatted);

    // Determine which field to use for grouping based on useApplicationType flag
    const appKey = useApplicationType ? application_type : application;

    // Reverse hierarchies so they go from top-level (L11+) down to direct manager (L7)
    const reversedLoginHierarchy = [...user_manager_login_hierarchy].reverse();
    const reversedNameHierarchy = [...user_manager_name_hierarchy].reverse();
    const reversedLevelHierarchy = [...user_manager_job_level_hierarchy].reverse();
    const reversedTeamSizeHierarchy = [...team_size_rollup].reverse();

    // Walk the reversed hierarchy and attribute metrics to each manager at target levels
    for (let i = 0; i < reversedLoginHierarchy.length; i++) {
      const managerLogin = reversedLoginHierarchy[i];
      const managerName = reversedNameHierarchy[i];
      const levelValue = reversedLevelHierarchy[i];
      const team_size = reversedTeamSizeHierarchy[i];
      const level = typeof levelValue === 'string' ? parseInt(levelValue, 10) : typeof levelValue === 'number' ? levelValue : NaN;

      // Skip if not a target level or missing data or NaN
      if (isNaN(level) || !targetLevels.includes(level) || !managerLogin || !managerName) {
        continue;
      }

      // Build the path for this manager (from top-level down to this manager)
      const path = reversedLoginHierarchy.slice(0, i + 1);

      // Initialize manager entry if not exists
      if (!managerMap.has(managerLogin)) {
        managerMap.set(managerLogin, {
          login: managerLogin,
          name: managerName,
          level,
          path,
          team_size: team_size,
          apps: new Map(),
          allUsers: new Set(),
          monthlyViews: new Map(),
          monthlyUsers: new Map()
        });
      }

      const manager = managerMap.get(managerLogin)!;

      // Update team_size if current record has a valid value AND (existing is undefined OR current is larger)
      // This handles cases where team_size_rollup array is shorter than manager hierarchy (old/incomplete data)
      if (team_size !== undefined && (manager.team_size === undefined || team_size > manager.team_size)) {
        manager.team_size = team_size;
      }

      // Initialize app entry if not exists (using appKey which is either application or application_type)
      if (!manager.apps.has(appKey)) {
        manager.apps.set(appKey, {
          views: 0,
          users: new Set(),
          monthlyViews: new Map(),
          monthlyUsers: new Map()
        });
      }

      const appMetrics = manager.apps.get(appKey)!;
      appMetrics.views += measure_value;
      appMetrics.users.add(user_login);

      // Track monthly views for app
      appMetrics.monthlyViews.set(create_date_formatted, (appMetrics.monthlyViews.get(create_date_formatted) || 0) + measure_value);

      // Track monthly unique users for app
      if (!appMetrics.monthlyUsers.has(create_date_formatted)) {
        appMetrics.monthlyUsers.set(create_date_formatted, new Set());
      }
      appMetrics.monthlyUsers.get(create_date_formatted)!.add(user_login);

      // Track total unique users for manager (across all apps)
      manager.allUsers.add(user_login);

      // Track monthly views for manager
      manager.monthlyViews.set(create_date_formatted, (manager.monthlyViews.get(create_date_formatted) || 0) + measure_value);

      // Track monthly unique users for manager
      if (!manager.monthlyUsers.has(create_date_formatted)) {
        manager.monthlyUsers.set(create_date_formatted, new Set());
      }
      manager.monthlyUsers.get(create_date_formatted)!.add(user_login);
    }
  }

  // Sort months chronologically
  const sortedMonths = sortDistinctMonths_MM_YYYY(Array.from(allMonths));

  // ===== SECOND PASS: Process Manager Roster Data =====
  // Add managers from roster data who have no user metrics but share a common ancestor
  // with managers who have actual metrics data
  if (managerRosterData && managerRosterData.length > 0) {
    // Collect set of valid manager logins from first pass (managers with actual metrics)
    const validManagerLogins = new Set<string>(managerMap.keys());

    // Debug: Log roster data structure
    console.log('[ManagerRoster] Processing roster data, count:', managerRosterData.length);
    if (managerRosterData.length > 0) {
      console.log('[ManagerRoster] Sample record:', {
        user_login: managerRosterData[0].user_login,
        manager_name: managerRosterData[0].manager_name,
        manage_login: managerRosterData[0].manage_login,
        user_job_level: managerRosterData[0].user_job_level,
        team_size_rollup: managerRosterData[0].team_size_rollup,
        user_manager_login_hierarchy: managerRosterData[0].user_manager_login_hierarchy,
        user_manager_name_hierarchy: managerRosterData[0].user_manager_name_hierarchy,
        user_manager_job_level_hierarchy: managerRosterData[0].user_manager_job_level_hierarchy
      });
    }
    console.log('[ManagerRoster] Valid manager logins from main data:', Array.from(validManagerLogins).slice(0, 10), '...');

    // Process each roster record
    // IMPORTANT: In roster data, user_login is a subordinate, and the actual manager we want is:
    // - user_manager_login_hierarchy[0] (manager's login)
    // - user_manager_name_hierarchy[0] (manager's name)
    // - user_manager_job_level_hierarchy[0] (manager's level)
    // - team_size_rollup[0] (manager's team size)
    for (const record of managerRosterData) {
      const { team_size_rollup, user_manager_login_hierarchy, user_manager_name_hierarchy, user_manager_job_level_hierarchy } = record;

      // The roster manager is the FIRST element of user_manager hierarchy (direct manager of user_login)
      // NOT user_login itself (which is just a subordinate used to trigger this record)
      if (!user_manager_login_hierarchy.length || !user_manager_name_hierarchy.length) {
        continue;
      }

      const rosterManagerLogin = user_manager_login_hierarchy[0];
      const rosterManagerName = user_manager_name_hierarchy[0];
      const rosterManagerLevelStr = user_manager_job_level_hierarchy[0];
      const rosterManagerTeamSize = team_size_rollup.length > 0 ? team_size_rollup[0] : 0;

      // The roster manager's level
      const rosterManagerLevel =
        typeof rosterManagerLevelStr === 'string'
          ? parseInt(rosterManagerLevelStr, 10)
          : typeof rosterManagerLevelStr === 'number'
          ? rosterManagerLevelStr
          : NaN;

      // Skip if the roster manager isn't at a target level
      if (isNaN(rosterManagerLevel) || !targetLevels.includes(rosterManagerLevel)) {
        continue;
      }

      // Skip if this roster manager already exists in the main data (main data takes priority)
      if (managerMap.has(rosterManagerLogin)) {
        continue;
      }

      // The manager's hierarchy path is from their managers (index 1+), reversed to go top-down
      // e.g., if user_manager_login_hierarchy = [cnancy, debleeck, rstites, panosp, ajassy]
      // Then cnancy's path is [ajassy, panosp, rstites, debleeck, cnancy] (reversed [1:] + cnancy)
      const managersAbove = user_manager_login_hierarchy.slice(1); // Managers above the roster manager
      const namesAbove = user_manager_name_hierarchy.slice(1);
      const levelsAbove = user_manager_job_level_hierarchy.slice(1);
      const teamSizesAbove = team_size_rollup.slice(1);

      // Reverse to go from top-level down
      const reversedLoginHierarchy = [...managersAbove].reverse();
      const reversedNameHierarchy = [...namesAbove].reverse();
      const reversedLevelHierarchy = [...levelsAbove].reverse();
      const reversedTeamSizeHierarchy = [...teamSizesAbove].reverse();

      // Walk the hierarchy and check if any manager exists in our valid set
      // This determines if this roster manager has a connecting ancestor
      let hasValidAncestor = false;
      let firstValidAncestorIndex = -1;

      for (let i = 0; i < reversedLoginHierarchy.length; i++) {
        const managerLogin = reversedLoginHierarchy[i];
        if (validManagerLogins.has(managerLogin)) {
          hasValidAncestor = true;
          firstValidAncestorIndex = i;
          break;
        }
      }

      // Skip if no connecting ancestor found in the current result set
      if (!hasValidAncestor) {
        continue;
      }

      // First, add any intermediate managers in this hierarchy that might be missing
      for (let i = firstValidAncestorIndex; i < reversedLoginHierarchy.length; i++) {
        const managerLogin = reversedLoginHierarchy[i];
        const managerName = reversedNameHierarchy[i];
        const levelValue = reversedLevelHierarchy[i];
        const team_size = reversedTeamSizeHierarchy[i];
        const level = typeof levelValue === 'string' ? parseInt(levelValue, 10) : typeof levelValue === 'number' ? levelValue : NaN;

        // Skip if not a target level or missing data or NaN
        if (isNaN(level) || !targetLevels.includes(level) || !managerLogin || !managerName) {
          continue;
        }

        // Skip if this manager already exists
        if (managerMap.has(managerLogin)) {
          continue;
        }

        // Build the path for this manager (from top-level down to this manager)
        const path = reversedLoginHierarchy.slice(0, i + 1);

        // Add this manager with zero metrics and no applications
        managerMap.set(managerLogin, {
          login: managerLogin,
          name: managerName,
          level,
          path,
          team_size: team_size,
          apps: new Map(),
          allUsers: new Set(),
          monthlyViews: new Map(),
          monthlyUsers: new Map()
        });

        validManagerLogins.add(managerLogin);
      }

      // Now add the roster manager themselves
      // Their path is the reversed hierarchy above them + themselves
      const rosterManagerPath = [...reversedLoginHierarchy, rosterManagerLogin];

      console.log('[ManagerRoster] Adding roster manager:', {
        login: rosterManagerLogin,
        name: rosterManagerName,
        level: rosterManagerLevel,
        team_size: rosterManagerTeamSize,
        path: rosterManagerPath
      });

      managerMap.set(rosterManagerLogin, {
        login: rosterManagerLogin,
        name: rosterManagerName,
        level: rosterManagerLevel,
        path: rosterManagerPath,
        team_size: rosterManagerTeamSize,
        apps: new Map(), // No applications for roster managers
        allUsers: new Set(), // No users
        monthlyViews: new Map(), // No monthly views
        monthlyUsers: new Map() // No monthly users
      });

      // Mark this roster manager as valid so subsequent records can connect to them
      validManagerLogins.add(rosterManagerLogin);
    }
  }

  // Build the result array with manager rows and application child rows
  const result: HierarchyRow[] = [];

  // Sort managers by level (descending) then by name
  const sortedManagers = Array.from(managerMap.values()).sort((a, b) => {
    if (b.level !== a.level) return b.level - a.level;
    return a.name.localeCompare(b.name);
  });

  for (const manager of sortedManagers) {
    // Calculate total views across all apps
    let totalViews = 0;
    manager.apps.forEach((metrics) => {
      totalViews += metrics.views;
    });

    // Add manager row with monthly data flattened
    const managerRow: ManagerHierarchyRow = {
      type: 'manager',
      path: manager.path,
      manager_login: manager.login,
      manager_name: manager.name,
      team_size: manager.team_size,
      manager_level: manager.level,
      total_views: totalViews,
      unique_users: manager.allUsers.size
    };

    // Add flattened monthly data for manager (e.g., "Dec 2025_views": 100)
    for (const month of sortedMonths) {
      managerRow[`${month}_views`] = manager.monthlyViews.get(month) || 0;
      managerRow[`${month}_users`] = manager.monthlyUsers.get(month)?.size || 0;
    }

    result.push(managerRow);

    // Add application rows as children (sorted by views descending)
    const sortedApps = Array.from(manager.apps.entries()).sort((a, b) => b[1].views - a[1].views);

    for (const [appName, metrics] of sortedApps) {
      const appRow: ApplicationHierarchyRow = {
        type: 'application',
        path: [...manager.path, appName], // Extends manager path with app name
        application: appName,
        views: metrics.views,
        unique_users: metrics.users.size
      };

      // Add flattened monthly data for app (e.g., "Dec 2025_views": 50)
      for (const month of sortedMonths) {
        appRow[`${month}_views`] = metrics.monthlyViews.get(month) || 0;
        appRow[`${month}_users`] = metrics.monthlyUsers.get(month)?.size || 0;
      }

      result.push(appRow);
    }
  }

  return result;
};

/**
 * Filters hierarchy data by manager level with top-down approach.
 * Shows managers at the selected level and all levels below (subordinates).
 * For example, selecting L10 shows L10, L9, L8, L7 managers.
 *
 * IMPORTANT: This function also TRIMS paths so that filtered-out ancestors
 * don't create empty placeholder nodes. When filtering to L7, L7 managers
 * become root nodes directly.
 *
 * @param hierarchyData - Array of HierarchyRow objects
 * @param maxLevel - Maximum manager level to include (e.g., 10 for L10 and below)
 * @returns Filtered array of HierarchyRow objects with trimmed paths
 */
export const filterHierarchyByLevel = (hierarchyData: HierarchyRow[], maxLevel: number): HierarchyRow[] => {
  // Build a set of manager logins that pass the filter
  const validManagerLogins = new Set<string>();

  for (const row of hierarchyData) {
    if (row.type === 'manager' && row.manager_level <= maxLevel) {
      validManagerLogins.add(row.manager_login);
    }
  }

  // Filter and transform rows - trim paths to remove filtered-out ancestors
  const result: HierarchyRow[] = [];

  for (const row of hierarchyData) {
    if (row.type === 'manager') {
      // Skip managers above the max level
      if (row.manager_level > maxLevel) {
        continue;
      }

      // Trim the path to only include valid (non-filtered) managers
      // Find the first index where a valid manager exists
      let trimStartIndex = 0;
      for (let i = 0; i < row.path.length; i++) {
        if (validManagerLogins.has(row.path[i])) {
          trimStartIndex = i;
          break;
        }
      }

      // Create new row with trimmed path
      const trimmedPath = row.path.slice(trimStartIndex);
      result.push({
        ...row,
        path: trimmedPath
      });
    } else {
      // Application row - check if parent manager passes the filter
      const parentManagerLogin = row.path[row.path.length - 2];
      if (!validManagerLogins.has(parentManagerLogin)) {
        continue;
      }

      // Trim the path similarly
      let trimStartIndex = 0;
      for (let i = 0; i < row.path.length - 1; i++) {
        // -1 because last element is the app type, not a manager
        if (validManagerLogins.has(row.path[i])) {
          trimStartIndex = i;
          break;
        }
      }

      // Create new row with trimmed path
      const trimmedPath = row.path.slice(trimStartIndex);
      result.push({
        ...row,
        path: trimmedPath
      });
    }
  }

  return result;
};

/**
 * Gets unique manager levels from hierarchy data for filter dropdown options.
 *
 * @param hierarchyData - Array of HierarchyRow objects
 * @returns Sorted array of unique manager levels (descending)
 */
export const getUniqueManagerLevels = (hierarchyData: HierarchyRow[]): number[] => {
  const levels = new Set<number>();

  for (const row of hierarchyData) {
    if (row.type === 'manager') {
      levels.add(row.manager_level);
    }
  }

  return Array.from(levels).sort((a, b) => b - a);
};

/**
 * Builds hierarchical data for the Application Hierarchy Tab.
 * Creates a tree structure where Application types are parent nodes and Managers are children.
 * Each user's metrics (views from measure_value) are attributed to ALL Applications + 'L7+' managers in their hierarchy.
 * Unique users are tracked per manager+app combination using Sets to ensure proper deduplication.
 *
 * @param userMetricsData - Parsed user metrics data with hierarchy information
 * @param targetLevels - Array of job levels to include (default: [7, 8, 9, 10, 11])
 * @param useApplicationType - If true, aggregates by application_type; if false, uses application (sub-app). Default: false
 * @returns Array of HierarchyRow objects for AG-Grid tree data
 */
export const buildApplicationFirstHierarchyData = (
  userMetricsData: UserMetricsParsedData[],
  targetLevels: number[] = MANAGER_TARGET_JOB_LEVELS,
  useApplicationType = false
): HierarchyRow[] => {
  // Filter out ALL_APPLICATIONS entries
  const filteredData = userMetricsData.filter((item) => item.application !== ALL_APPLICATIONS);

  //Track all months present in data (generally last 6)
  const allMonths = new Set<string>();

  //Map Structure: Application (or application_type) -> {app info, manager: Map<manager_login, {manager info, level, views, users Set, monthlyData}>}, allUsers: Set, monthly Data}
  const appMap = new Map<
    string,
    {
      appName: string;
      managerTree: Map<
        string,
        {
          login: string;
          name: string;
          level: number;
          team_size: number;
          path: string[];
          views: number;
          users: Set<string>;
          monthlyViews: Map<string, number>;
          monthlyUsers: Map<string, Set<string>>;
        }
      >;
      totalViews: number;
      allUsers: Set<string>;
      monthlyViews: Map<string, number>;
      monthlyUsers: Map<string, Set<string>>;
    }
  >();

  // Process each user record
  for (const record of filteredData) {
    const {
      application,
      application_type,
      measure_value,
      user_login,
      team_size_rollup,
      create_date_formatted,
      user_manager_login_hierarchy,
      user_manager_name_hierarchy,
      user_manager_job_level_hierarchy
    } = record;

    allMonths.add(create_date_formatted);

    // Determine which field to use for grouping based on useApplicationType flag
    const appKey = useApplicationType ? application_type : application;

    // Reverse hierarchies so they go from top-level (L11+) down to direct manager (L7)
    const reversedLoginHierarchy = [...user_manager_login_hierarchy].reverse();
    const reversedNameHierarchy = [...user_manager_name_hierarchy].reverse();
    const reversedLevelHierarchy = [...user_manager_job_level_hierarchy].reverse();
    const reversedTeamSizeHierarchy = [...team_size_rollup].reverse();

    if (!appMap.has(appKey)) {
      appMap.set(appKey, {
        appName: appKey,
        managerTree: new Map(),
        totalViews: 0,
        allUsers: new Set(),
        monthlyViews: new Map(),
        monthlyUsers: new Map()
      });
    }

    const app = appMap.get(appKey)!; // Add ! since we know it exists

    app.allUsers.add(user_login);

    app.totalViews += measure_value;
    if (!app.monthlyUsers.has(create_date_formatted)) {
      app.monthlyUsers.set(create_date_formatted, new Set());
    }
    app.monthlyUsers.get(create_date_formatted)!.add(user_login);

    app.monthlyViews.set(create_date_formatted, (app.monthlyViews.get(create_date_formatted) || 0) + measure_value);

    // Build a filtered path that only includes managers at target levels
    const filteredPath: string[] = [];
    const filteredPathLevels: number[] = [];

    for (let i = 0; i < reversedLoginHierarchy.length; i++) {
      const managerLogin = reversedLoginHierarchy[i];
      const managerName = reversedNameHierarchy[i];
      const levelValue = reversedLevelHierarchy[i];
      const team_size = reversedTeamSizeHierarchy[i];
      const level = typeof levelValue === 'string' ? parseInt(levelValue, 10) : typeof levelValue === 'number' ? levelValue : NaN;

      // Only add to filtered path if at target level
      if (!isNaN(level) && targetLevels.includes(level) && managerLogin && managerName) {
        filteredPath.push(managerLogin);
        filteredPathLevels.push(level);
      }
    }

    // Now process each manager in the filtered path
    for (let i = 0; i < filteredPath.length; i++) {
      const managerLogin = filteredPath[i];
      const level = filteredPathLevels[i];

      // Find the manager name from the original hierarchy
      const originalIndex = reversedLoginHierarchy.indexOf(managerLogin);
      const managerName = reversedNameHierarchy[originalIndex];
      const team_size = reversedTeamSizeHierarchy[originalIndex];

      // Build path using only filtered (target-level) managers up to this point
      const path = filteredPath.slice(0, i + 1);

      if (!app.managerTree.has(managerLogin)) {
        app.managerTree.set(managerLogin, {
          // Changed appMap to app
          login: managerLogin,
          name: managerName,
          level: level,
          path: path,
          team_size: team_size,
          views: 0, // Initialize to 0
          users: new Set<string>(), // Initialize empty Set
          monthlyViews: new Map(), // Initialize empty Map
          monthlyUsers: new Map() // Initialize empty Map
        });
      }

      const managerMetrics = app.managerTree.get(managerLogin)!;

      // Update team_size if current record has a valid value AND (existing is undefined OR current is larger)
      // This handles cases where team_size_rollup array is shorter than manager hierarchy (old/incomplete data)
      if (team_size !== undefined && (managerMetrics.team_size === undefined || team_size > managerMetrics.team_size)) {
        managerMetrics.team_size = team_size;
      }

      managerMetrics.views += measure_value;
      managerMetrics.users.add(user_login);

      managerMetrics.monthlyViews.set(create_date_formatted, (managerMetrics.monthlyViews.get(create_date_formatted) || 0) + measure_value);

      if (!managerMetrics.monthlyUsers.has(create_date_formatted)) {
        managerMetrics.monthlyUsers.set(create_date_formatted, new Set());
      }
      managerMetrics.monthlyUsers.get(create_date_formatted)!.add(user_login);
    }
  }

  // Sort months chronologically
  const sortedMonths = sortDistinctMonths_MM_YYYY(Array.from(allMonths));

  // Build the result array with manager rows and application child rows
  const result: HierarchyRow[] = [];

  // Sort apps alphabetically
  const sortedApps = Array.from(appMap.values()).sort((a, b) => a.appName.localeCompare(b.appName));

  for (const app of sortedApps) {
    // Add application row (root level)
    const appRow: ApplicationHierarchyRow = {
      type: 'application',
      path: [app.appName], // App is root, so path is just [appName]
      application: app.appName,
      views: app.totalViews,
      unique_users: app.allUsers.size
    };

    // Add monthly data for app
    for (const month of sortedMonths) {
      appRow[`${month}_views`] = app.monthlyViews.get(month) || 0;
      appRow[`${month}_users`] = app.monthlyUsers.get(month)?.size || 0;
    }

    result.push(appRow);

    // Add manager rows as children (sorted by level desc, then name)
    const sortedManagers = Array.from(app.managerTree.values()).sort((a, b) => {
      if (b.level !== a.level) return b.level - a.level;
      return a.name.localeCompare(b.name);
    });

    for (const manager of sortedManagers) {
      const managerRow: ManagerHierarchyRow = {
        type: 'manager',
        path: [app.appName, ...manager.path], // Prepend app to manager's path
        manager_login: manager.login,
        manager_name: manager.name,
        team_size: manager.team_size,
        manager_level: manager.level,
        total_views: manager.views,
        unique_users: manager.users.size
      };

      // Add monthly data for manager
      for (const month of sortedMonths) {
        managerRow[`${month}_views`] = manager.monthlyViews.get(month) || 0;
        managerRow[`${month}_users`] = manager.monthlyUsers.get(month)?.size || 0;
      }

      result.push(managerRow);
    }
  }

  return result;
};

/**
 * Aggregated user detail record returned from filterAndAggregateUserDetails
 */
export interface AggregatedUserDetail {
  user_login: string;
  application: string;
  application_type: string;
  team_name: string;
  active_since: string;
  last_active: string;
  measure_value: number;
  user_manager_name_hierarchy: string[];
}

/**
 * Filters user metrics data by manager name (and optionally sub-application),
 * with optional aggregation by user_login + application (sub-app) OR application_type.
 *
 * @param data - Source user metrics data
 * @param managerName - Manager name to filter by (returns empty if null)
 * @param applicationOrType - Sub-application name to filter by (only used when isManagerView is false)
 * @param isManagerView - True for Manager Hierarchy view, false for Application Hierarchy view
 * @param shouldAggregate - True to aggregate; when true and isManagerView=true aggregates by application_type,
 *                         when true and isManagerView=false aggregates by application (sub-app)
 * @returns Filtered (and optionally aggregated) user details
 */
export const filterAndAggregateUserDetails = (
  data: UserMetricsParsedData[] | null | undefined,
  managerName: string | null,
  applicationOrType: string | null,
  isManagerView: boolean,
  shouldAggregate: boolean
): AggregatedUserDetail[] | UserMetricsParsedData[] => {
  if (!managerName || !data) {
    return [];
  }

  let baseData: UserMetricsParsedData[];

  if (isManagerView) {
    // Manager View: filter by manager only
    baseData = data.filter((record) => record.user_manager_name_hierarchy.includes(managerName));
  } else {
    // Application View: filter by manager AND application
    // When shouldAggregate is TRUE (Application mode) → filter by application_type
    // When shouldAggregate is FALSE (Sub-Application mode) → filter by application (sub-app)
    if (shouldAggregate) {
      // Application mode - subApplication holds application_type
      baseData = data.filter((record) => record.user_manager_name_hierarchy.includes(managerName) && record.application_type === applicationOrType);
    } else {
      // Sub-Application mode - subApplication holds specific application name
      baseData = data.filter((record) => record.user_manager_name_hierarchy.includes(managerName) && record.application === applicationOrType);
    }
  }

  // Always aggregate to combine monthly records
  // shouldAggregate=true (Application mode) → group by user_login + application_type (fewer rows)
  // shouldAggregate=false (Sub-Application mode) → group by user_login + application (more rows)
  const aggregationMap = new Map<string, AggregatedUserDetail>();

  for (const record of baseData) {
    // Choose aggregation key based on mode
    const appField = shouldAggregate ? record.application_type : record.application;
    const key = `${record.user_login}|${appField}`;

    if (aggregationMap.has(key)) {
      const existing = aggregationMap.get(key)!;
      // Sum measure_value
      existing.measure_value += record.measure_value;
      // Keep earliest active_since
      if (record.active_since && (!existing.active_since || record.active_since < existing.active_since)) {
        existing.active_since = record.active_since;
      }
      // Keep latest last_active
      if (record.last_active && (!existing.last_active || record.last_active > existing.last_active)) {
        existing.last_active = record.last_active;
      }
    } else {
      aggregationMap.set(key, {
        user_login: record.user_login,
        application: record.application,
        application_type: record.application_type,
        team_name: record.team_name,
        active_since: record.active_since,
        last_active: record.last_active,
        measure_value: record.measure_value,
        user_manager_name_hierarchy: record.user_manager_name_hierarchy
      });
    }
  }

  return Array.from(aggregationMap.values());
};

/**
 * Builds column definitions for the hierarchy grid.
 * Generates base columns, monthly column groups, and total columns.
 *
 * @param availableMonths - Array of month strings (e.g., ['Dec-2025', 'Jan-2026'])
 * @param hierarchyViewMode - Current view mode (Manager or Application)
 * @param detailCellRenderer - Cell renderer component for the "View Users" button
 * @param showViews - If true, shows Views columns and hides Users columns; if false, shows Users and hides Views
 * @returns Array of column definitions for AG-Grid
 */
export const buildHierarchyColumnDefs = (
  availableMonths: string[],
  hierarchyViewMode: HierarchyViewMode,
  detailCellRenderer: React.ComponentType<any>,
  showViews = false
): (ColDef | ColGroupDef)[] => {
  // Base columns - always present
  const baseColumns: (ColDef | ColGroupDef)[] = [
    {
      colId: 'manager_name_export',
      headerName: 'Manager Name',
      hide: true, // Hidden from UI, used for export
      valueGetter: (params: ValueGetterParams) => {
        if (params.data?.type === 'manager') {
          return params.data.manager_name;
        }
        return ''; // Empty for application rows
      }
    },
    {
      field: 'manager_level',
      headerName: 'Level',
      width: 80,
      valueGetter: (params: ValueGetterParams) => {
        if (params.data?.type === 'manager') {
          return `L${params.data.manager_level}`;
        }
        return '';
      }
    },
    {
      field: 'team_size',
      headerName: 'Team Size',
      width: 100,
      // hide: hierarchyViewMode !== HIERARCHY_VIEW_MODE.MANAGER, // Only show in Manager View
      valueGetter: (params: ValueGetterParams) => {
        if (params.data?.type === 'manager') {
          return `${params.data.team_size}`;
        }
        return '';
      }
    }
  ];

  // Generate monthly column groups (Views/Users for each month)
  // Visibility controlled by showViews parameter
  const monthlyColumns: ColGroupDef[] = availableMonths.map((month) => ({
    headerName: month,
    children: [
      {
        field: `${month}_views`,
        headerName: 'Views',
        hide: !showViews, // Show when showViews is true
        width: 100,
        valueFormatter: (params: ValueFormatterParams) => (params.value ? numberFormatter(params.value) : '0')
      },
      {
        field: `${month}_users`,
        headerName: 'Users',
        hide: showViews, // Hide when showViews is true
        width: 100,
        valueFormatter: (params: ValueFormatterParams) => (params.value ? numberFormatter(params.value) : '0')
      }
    ]
  }));

  // Total columns group - sum of all monthly values
  // Visibility controlled by showViews parameter
  const totalColumns: ColGroupDef = {
    headerName: 'Total',
    children: [
      {
        colId: 'total_views_calculated',
        headerName: 'Views',
        width: 100,
        hide: !showViews, // Show when showViews is true
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data) return 0;
          let sum = 0;
          Object.keys(params.data).forEach((key) => {
            // Sum all _views columns except total_views
            if (key.endsWith('_views') && key !== 'total_views') {
              sum += params.data[key] || 0;
            }
          });
          return sum;
        },
        valueFormatter: (params: ValueFormatterParams) => (params.value ? numberFormatter(params.value) : '0')
      },
      {
        colId: 'total_users_calculated',
        headerName: 'Users',
        width: 100,
        hide: showViews, // Hide when showViews is true
        valueGetter: (params: ValueGetterParams) => {
          // Use pre-calculated unique_users from buildManagerHierarchyData
          // This represents true unique users across all months (not sum of monthly counts)
          return params.data?.unique_users || 0;
        },
        valueFormatter: (params: ValueFormatterParams) => (params.value ? numberFormatter(params.value) : '0')
      }
    ]
  };

  return [...baseColumns, totalColumns, ...monthlyColumns];
};

export const filterApplicationHierarchyByLevel = (hierarchyData: HierarchyRow[], maxLevel: number): HierarchyRow[] => {
  // In Application View:
  // - Application rows (root): Always include
  // - Manager rows (children): Filter by level, trim path if needed

  const validManagerLogins = new Set<string>();

  // First pass: identify valid managers
  for (const row of hierarchyData) {
    if (row.type === 'manager' && row.manager_level <= maxLevel) {
      validManagerLogins.add(row.manager_login);
    }
  }

  const result: HierarchyRow[] = [];

  for (const row of hierarchyData) {
    if (row.type === 'application') {
      // Always include application (root) rows
      result.push(row);
    } else if (row.type === 'manager') {
      // Filter managers by level
      if (row.manager_level > maxLevel) continue;

      // Path structure: [appType, manager1, manager2, ...]
      // Trim to keep appType + valid managers only
      const appType = row.path[0];
      const managerPath = row.path.slice(1);

      let trimStartIndex = 0;
      for (let i = 0; i < managerPath.length; i++) {
        if (validManagerLogins.has(managerPath[i])) {
          trimStartIndex = i;
          break;
        }
      }

      const trimmedManagerPath = managerPath.slice(trimStartIndex);
      result.push({
        ...row,
        path: [appType, ...trimmedManagerPath]
      });
    }
  }

  return result;
};

/**
 * Returns two lists of UserManagerParsed Data, One where the application/applicationType == MANAGER_ROSTER
 * And the other is for everything else
 * @param fullData
 */
export const returnManagerRosterData = (fullData: UserMetricsParsedData[]): [UserMetricsParsedData[], UserMetricsParsedData[]] => {
  // MANAGER_ROSTER constant value is 'Manager Roster' (with space)
  const managerRosterData = fullData.filter((item) => item.application_type === MANAGER_ROSTER);
  const nonManagerRosterData = fullData.filter((item) => item.application_type !== MANAGER_ROSTER);
  return [managerRosterData, nonManagerRosterData];
};

/**
 * For the home page user metrics widget
 * Displays the month data for unique users with application = DasFinSuite-website
 * @params fullData
 */
export const returnFinSuiteUniqueUsersData = (fullData: UserMetricsParsedData[]): UserMetricsHomePageFinSuite[] => {
  //First step is to filter for application = DasFinSuite-website
  const filteredData = fullData.filter((item) => item.application === DAS_FIN_SUITE_WEBSITE);
  //Next, for each month we keep a set of user_logins
  const monthUserMap = new Map<string, Set<string>>();
  filteredData.forEach((item) => {
    const month = item.log_month;
    if (!monthUserMap.has(month)) {
      monthUserMap.set(month, new Set<string>());
    }
    monthUserMap.get(month)!.add(item.user_login);
  });
  //Lastly we return a list of objects counting the number of unique users in the set and returning
  const result: UserMetricsHomePageFinSuite[] = [];
  monthUserMap.forEach((userSet, month) => {
    result.push({
      month,
      unique_users: userSet.size
    });
  });
  // Return sorted by Date descending
  return result.sort((a, b) => b.month.localeCompare(a.month));
};
